import { BaseElement } from 'base';

class ColorPicker extends BaseElement {
  #action = null;
  #committed = null;
  #epoch = 0;
  #pending = null;

  onConnect() {
    this.#pending = null;
    this.removeAttribute('aria-busy');
    this.#committed = this.#current;

    this.listen(this, 'click', this.#onClick);
  }

  run(action) {
    this.#action = action;
    return this;
  }

  get #links() {
    return this.qa('a.color-swatch-control[data-product-id]');
  }

  get #current() {
    return (
      this.#links.find(link => link.getAttribute('aria-current') === 'true') ??
      null
    );
  }

  #onClick(event) {
    if (!this.#action || event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    const link = this.#links.find(candidate =>
      candidate.contains(event.target)
    );
    if (!link) return;

    event.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;

    this.#epoch += 1;
    const epoch = this.#epoch;
    this.#apply(link);
    this.#runAction(link, epoch);
  }

  #apply(link) {
    const group = link.dataset.group;

    for (const candidate of this.#links) {
      if (candidate === link) candidate.setAttribute('aria-current', 'true');
      else candidate.removeAttribute('aria-current');
    }

    for (const valueGroup of this.qa('[data-picker-value-group][data-group]')) {
      valueGroup.hidden = valueGroup.dataset.group !== group;
    }

    const scope = `[data-picker-value-group][data-group="${group}"]`;
    const value = this.q(`${scope} [data-picker-value]`);
    if (value) value.textContent = link.dataset.colorName ?? '';

    const suffix = this.q(`${scope} [data-picker-suffix]`);
    if (suffix) suffix.hidden = link.dataset.soldOut !== 'true';
  }

  async #runAction(link, epoch) {
    const committed = this.#committed;
    const change = {
      href: link.href,
      productId: Number(link.dataset.productId),
      previous: committed ? Number(committed.dataset.productId) : null,
      target: this.#targetOf(link)
    };

    let pending = null;
    try {
      pending = this.#action(change);
      if (pending && typeof pending.then === 'function') {
        this.#pending = pending;
        this.setAttribute('aria-busy', 'true');
        await pending;
      }
      if (this.isConnected && this.#epoch === epoch) this.#committed = link;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[color-picker] action failed:', error);
      if (!this.isConnected || this.#epoch !== epoch) return;
      this.#rollback(link);
    } finally {
      if (this.isConnected && this.#pending === pending) {
        this.#pending = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #targetOf(link) {
    const { optionNames, colorIndex, variants } = link.dataset;
    if (!optionNames || !variants) return null;

    try {
      const names = JSON.parse(optionNames);
      const tuples = JSON.parse(variants);
      if (!Array.isArray(names) || !Array.isArray(tuples)) return null;
      return {
        names,
        colorIndex: parseInt(colorIndex, 10),
        variants: tuples.map(([id, options, available]) => ({
          id,
          options,
          available
        }))
      };
    } catch (error) {
      console.error('[color-picker] invalid variant data:', error);
      return null;
    }
  }

  #rollback(link) {
    const committed = this.#committed;
    if (!committed) return;

    const hadFocus = link === this.getRootNode().activeElement;
    this.#apply(committed);
    if (hadFocus) committed.focus();
  }
}

if (!customElements.get('color-picker'))
  customElements.define('color-picker', ColorPicker);
